import { ChangeEvent, DetailedHTMLProps, forwardRef, HTMLAttributes, InputHTMLAttributes, memo, useEffect, useId, useMemo, useRef } from "react";
import classNames from "classnames";
import { RefType } from "../../../../types";
import { useMergedRef } from "../../../../utils";

import { ReactComponent as CheckedIcon } from "./icons/check.svg";
import { ReactComponent as MinusIcon } from "./icons/minus.svg";

import styles from "./style.module.scss";

export type CheckboxBaseSize = "sm" | "md";


export type CheckboxBaseProps = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "value" | "onChange" | "ref"> & {
  indeterminate?: boolean,
  value?: boolean,
  checked?: boolean,
  onChange?: (value: boolean, e: ChangeEvent<HTMLInputElement>) => void,
  checkboxBaseSize?: CheckboxBaseSize,
  containerProps?: DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>
  inputRef?: RefType<HTMLInputElement>,
  inputState?: unknown
  hintText?: unknown
}

export const CheckboxBase = memo(forwardRef<HTMLSpanElement, CheckboxBaseProps>((
  {
    id,
    indeterminate,
    value,
    checked,
    disabled,
    onChange,
    className,
    containerProps,
    inputRef,
    checkboxBaseSize = "md",
    inputState,
    hintText,
    ...props
  },
  ref,
) => {
  const resolvedRef = useRef<HTMLInputElement>();
  const reactId = useId();
  const realId = id || reactId;
  const isChecked = (value === undefined ? (checked) : value) || false;
  const mergedInputRef = useMergedRef(resolvedRef, inputRef);

  const onValueChanged = useMemo(() => (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange?.(e.target.checked, e);
    }
  }, [ disabled, onChange ]);

  if (resolvedRef.current) {
    (resolvedRef.current as any).indeterminate = indeterminate;
  }

  return (
    <span
      {...containerProps}
      className={classNames(
        styles.root,
        className,
        styles[checkboxBaseSize as keyof typeof styles],
      )}
      data-input-type="checkbox"
      data-input-state={indeterminate ? "indeterminate" : isChecked}
      data-input-disabled={disabled}
      data-checkbox-base-size={checkboxBaseSize}
      ref={ref}
    >
      <input
        {...props}
        id={realId}
        ref={mergedInputRef}
        value={`${isChecked}`}
        checked={isChecked}
        disabled={disabled}
        onChange={onValueChanged}
        type="checkbox"
      />
      <span role="button" className={styles.icon}>
        {
          indeterminate
            ? <MinusIcon />
            : (
              isChecked
                ? <CheckedIcon />
                : null
            )
        }
      </span>
    </span>
  );
}));